import { FC, useMemo, useState } from 'react';
import { Grid, Typography, Box, TextField, MenuItem, Chip, CircularProgress, Alert, Card, CardContent } from '@mui/material';
import { useGuardianState } from '../../../../contexts/guardian.context';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useGuardianLibraryHook } from '../../../../hooks/useGuardianAcademicHook';
import { useAcademicsFiltersContext } from '../../../../../academics/contexts/academicsFilters.context';
import PaginatedGrid from '../../../../../../components/paginatedGrid/PaginatedGrid';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { CheckCircle, Cancel } from '@mui/icons-material';

const GuardianLibraryView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedChildId } = useGuardianState();
  const { academicYear, grade } = useAcademicsFiltersContext();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const { data, isLoading, error } = useGuardianLibraryHook(
    selectedChildId || '',
    {
      page,
      limit: pageSize,
      ...(academicYear && { academicYear }),
      ...(grade && { grade }),
    }
  );

  const books = useMemo(() => data?.data || [], [data]);
  // const totalCount = data?.count || 0;

  const filteredBooks = useMemo(() => {
    if (statusFilter === 'all') return books;
    if (statusFilter === 'borrowed') return books.filter((b) => !b.available);
    if (statusFilter === 'available') return books.filter((b) => b.available);
    return books;
  }, [books, statusFilter]);

  const columns = useMemo<GridColDef[]>(() => [
    {
      field: 'title',
      headerName: translateText('guardian.academics.library.columns.title'),
      width: 250,
      flex: 1,
    },
    {
      field: 'author',
      headerName: translateText('guardian.academics.library.columns.author'),
      width: 200,
      flex: 1,
    },
    {
      field: 'isbn',
      headerName: translateText('guardian.academics.library.columns.isbn'),
      width: 150,
    },
    {
      field: 'available',
      headerName: translateText('guardian.academics.library.columns.status'),
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const isAvailable = params.value as boolean;
        return (
          <Box display="flex" alignItems="center" gap={1}>
            {isAvailable ? (
              <>
                <CheckCircle sx={{ color: 'success.main', fontSize: 18 }} />
                <Chip
                  label={translateText('guardian.academics.library.status.available')}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              </>
            ) : (
              <>
                <Cancel sx={{ color: 'error.main', fontSize: 18 }} />
                <Chip
                  label={translateText('guardian.academics.library.status.borrowed')}
                  size="small"
                  color="error"
                  variant="outlined"
                />
              </>
            )}
          </Box>
        );
      },
    },
    {
      field: 'dueDate',
      headerName: translateText('guardian.academics.library.columns.dueDate'),
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const dueDate = params.row.dueDate;
        if (!dueDate) return '-';
        const date = new Date(dueDate);
        const today = new Date();
        const isOverdue = date < today;
        return (
          <Typography
            variant="body2"
            color={isOverdue ? 'error' : 'text.primary'}
            fontWeight={isOverdue ? 600 : 400}
          >
            {date.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
          </Typography>
        );
      },
    },
  ], [translateText]);

  const rows = useMemo(() => {
    return filteredBooks.map((book) => ({
      id: book.id,
      title: book.title,
      author: book.author,
      isbn: book.isbn,
      available: book.available,
      dueDate: book.dueDate,
    }));
  }, [filteredBooks]);

  const handlePageChange = (newPage: number, newPageSize: number) => {
    setPage(newPage);
    setPageSize(newPageSize);
  };

  const borrowedBooks = useMemo(() => books.filter((b) => !b.available), [books]);
  // const availableBooks = books.filter((b) => b.available);

  if (!selectedChildId) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-library-container">
        <Grid item xs={12}>
          <Alert severity="info">{translateText('guardian.selectChild')}</Alert>
        </Grid>
      </Grid>
    );
  }

  if (isLoading) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-library-container">
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              {translateText('common.loading')}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-library-container">
        <Grid item xs={12}>
          <Alert severity="error">{translateText('common.error')}</Alert>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-library-container">
      <Grid item xs={12}>
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={2}
          mb={2}
          data-testid="guardian-library-filters"
        >
          <TextField
            select
            label={translateText('guardian.academics.library.filters.status')}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 150 }}
            size="small"
            data-testid="guardian-library-status-filter"
          >
            <MenuItem value="all">{translateText('common.all')}</MenuItem>
            <MenuItem value="borrowed">{translateText('guardian.academics.library.status.borrowed')}</MenuItem>
            <MenuItem value="available">{translateText('guardian.academics.library.status.available')}</MenuItem>
          </TextField>
        </Box>
      </Grid>

      {borrowedBooks.length > 0 && (
        <Grid item xs={12}>
          <Card sx={{ mb: 2 }} data-testid="guardian-library-borrowed-section">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {translateText('guardian.academics.library.borrowedBooks')} ({borrowedBooks.length})
              </Typography>
              <PaginatedGrid
                rows={borrowedBooks.map((book) => ({
                  id: book.id,
                  title: book.title,
                  author: book.author,
                  isbn: book.isbn,
                  available: book.available,
                  dueDate: book.dueDate,
                }))}
                columns={columns}
                loading={false}
                dataTestId="guardian-library-borrowed-grid"
                serverSide={false}
                totalCount={borrowedBooks.length}
                currentPage={1}
                currentPageSize={borrowedBooks.length}
                onPageChange={() => { }}
                rowsPerPageOptions={[5, 10, 25]}
              />
            </CardContent>
          </Card>
        </Grid>
      )}

      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          {translateText('guardian.academics.library.allBooks')}
        </Typography>
        <PaginatedGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          dataTestId="guardian-library-grid"
          serverSide={false}
          totalCount={filteredBooks.length}
          currentPage={page}
          currentPageSize={pageSize}
          onPageChange={handlePageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Grid>
    </Grid>
  );
};

export default GuardianLibraryView;
